/**
 * Central place to configure the status label/colors that appear on Board cards.
 * Update the `matches` arrays to map backend status ids to a label/color combo.
 */
import { LostStatuses } from "../../../types/LostStatuses";

export type StatusTheme = {
  key: string;
  label: string;
  matches: number[];
  border: string;
  background: string;
  pillColor: string;
  pillBackground: string;
};

const themes: StatusTheme[] = [
  // Drafts (incomplete + completed) — gray, low-attention. Both share the same neutral palette
  // since they're functionally "unfinished work" with no traffic-light meaning.
  {
    key: "draftIncomplete",
    label: "Draft Incomplete",
    matches: [LostStatuses.DraftIncomplete],
    border: "#D1D5DB",
    background: "#F9FAFB",
    pillColor: "#4B5563",
    pillBackground: "#E5E7EB",
  },
  {
    key: "draftCompleted",
    label: "Draft Completed",
    matches: [LostStatuses.DraftCompleted],
    border: "#D1D5DB",
    background: "#F9FAFB",
    pillColor: "#4B5563",
    pillBackground: "#E5E7EB",
  },
  {
    key: "notPaid",
    label: "Not Paid",
    matches: [LostStatuses.NotPaid],
    border: "#FACC15",
    background: "#FEFCE8",
    pillColor: "#A16207",
    pillBackground: "#FEF08A",
  },
  // Open — RED (traffic light: stop, brand new claims, attention needed)
  {
    key: "open",
    label: "Open",
    matches: [LostStatuses.Open],
    border: "#FCA5A5",
    background: "#FEE2E2",
    pillColor: "#B91C1C",
    pillBackground: "#FECACA",
  },
  {
    key: "pending",
    label: "Pending",
    matches: [LostStatuses.Pending],
    border: "#FFD48A",
    background: "#FFF7E5",
    pillColor: "#A05A00",
    pillBackground: "#FFE2B5",
  },
  {
    key: "urgent",
    label: "Urgent",
    matches: [LostStatuses.Urgent],
    border: "#EAB308",
    background: "#FDFDEA",
    pillColor: "#A8071A",
    pillBackground: "#FFE2E0",
  },
  // Review — AMBER (traffic light: caution, in progress). Per-card "okay to proceed" cards
  // (Review + outside refund window) override to GREEN via Ticket/styles.ts.
  {
    key: "review",
    label: "Review",
    matches: [LostStatuses.Review],
    border: "#FDE68A",
    background: "#FEF3C7",
    pillColor: "#B45309",
    pillBackground: "#FCD34D",
  },
  // In Process — GREEN. This column holds claims whose "fresh-payment" hold-off window
  // has expired and are now actionable. The shade matches the green Review used to flip
  // to when most of its cards were past the window, so admins reading "green = go" carry
  // the same mental model over to InProcess.
  {
    key: "inProcess",
    label: "In Process",
    matches: [LostStatuses.InProcess],
    border: "#86EFAC",       // green-300
    background: "#DCFCE7",   // green-100
    pillColor: "#047857",    // emerald-700
    pillBackground: "#BBF7D0", // green-200
  },
  // LF Notified — PURPLE (distinct from the traffic-light colors so admins instantly see
  // this column is in a different phase of the workflow).
  {
    key: "lfNotified",
    label: "LF Notified",
    matches: [LostStatuses.LFNotified],
    border: "#C4B5FD",
    background: "#EDE9FE",
    pillColor: "#6D28D9",
    pillBackground: "#DDD6FE",
  },
  {
    key: "overdue",
    label: "Overdue",
    matches: [LostStatuses.Overdue],
    border: "#FB923C",
    background: "#FFF7ED",
    pillColor: "#9A3412",
    pillBackground: "#FED7AA",
  },
  // Deleted — DARKER red than Open (archived/dead). Darker border so admins instantly
  // distinguish a deleted claim from a bright-red Open claim at a glance.
  // pillColor stays dark so the active filter-tab (which swaps fg/bg) reads correctly.
  {
    key: "deleted",
    label: "Deleted",
    matches: [LostStatuses.Deleted],
    border: "#991B1B",
    background: "#FECACA",
    pillColor: "#7F1D1D",
    pillBackground: "#FCA5A5",
  },
];

export const defaultStatusTheme = {
  label: "Open",
  border: "#E5E7EB",
  background: "#FFFFFF",
  pillColor: "#1F2937",
  pillBackground: "#F5F5F5",
};

export const getStatusTheme = (status?: number) => {
  if (typeof status !== "number") return defaultStatusTheme;
  return themes.find((theme) => theme.matches.includes(status)) || defaultStatusTheme;
};

export const getStatusLabel = (status?: number) => getStatusTheme(status).label;

export const statusThemes = themes;
